import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { getMessageApi, getNotificationApi } from './utils/antdApp';

function resolveApiBaseUrl() {
  const envApi =
    (process.env.REACT_APP_API_URL as string | undefined) ||
    (process.env.UMI_APP_API_URL as string | undefined) ||
    '';
  if (envApi) {
    return envApi.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // 兜底：生产域名下即使环境变量未注入，也强制走独立后端域名
    if (host.endsWith('.umicloud.ai')) {
      return 'https://api.umicloud.ai';
    }
  }
  return '';
}

const API_BASE_URL = resolveApiBaseUrl();

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      const message = getMessageApi();
      const notification = getNotificationApi();
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = localStorage.getItem('umi_admin_token');
      const headers = config.headers || {};
      let url = config.url || '';
      if (API_BASE_URL && /^\/api\//.test(url)) {
        url = `${API_BASE_URL}${url}`;
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      return { ...config, headers, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const message = getMessageApi();
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      if (data?.success === false) {
        message.error(data?.message || '请求失败！');
      }
      return response;
    },
  ],
};
